import { useEffect, useRef, useState } from 'react';
import { onMutation } from './mutationSignal';

export interface PolledState<T> {
  rows: T[];
  loading: boolean;
  error: Error | null;
}

/**
 * Replacement for `onSnapshot` (China B-mono P4). Fetches on mount + key
 * change, on tab/online regain, and whenever a write lands anywhere in the app
 * (`onMutation`) — that last edge is what makes a user's own change appear
 * instantly.
 *
 * Pas de polling périodique (issue #63) : Firestore facture chaque document
 * renvoyé, même inchangé, et un seul Dashboard resté ouvert la nuit coûtait
 * ~750 000 lectures/heure. Les changements des AUTRES apparaissent à la
 * prochaine navigation ou au retour sur l'onglet — choix assumé.
 * `key === null` is an idle state (no fetch) so callers can mirror the empty-id
 * early return of the SDK hooks. The fetcher returns already-shaped rows —
 * scope/query lives in the caller (and is enforced server-side).
 */
export function usePolledResource<T>(
  key: string | null,
  fetcher: () => Promise<T[]>,
): PolledState<T> {
  const [state, setState] = useState<PolledState<T>>({
    rows: [],
    loading: key !== null,
    error: null,
  });

  // Keep the latest fetcher without making it a re-subscribe trigger — the
  // caller re-creates the closure every render, but only `key` should reset.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (key === null) {
      setState({ rows: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // `running`/`again` keep exactly ONE load in flight per hook: every
    // trigger (tab regain, online, write) funnels through `load`.
    let running = false;
    let again = false;
    // Lignes héritées de la clé précédente : gardées pendant le chargement (pas de
    // flash), jetées si la nouvelle clé échoue avant d'avoir jamais chargé — sinon
    // le Rapport affichait le total de 2026 sous une période 2020-2026 en 503.
    let loadedOnce = false;
    setState((prev) => ({ ...prev, loading: true }));

    const load = async () => {
      // A trigger arriving mid-flight is remembered, not dropped: the in-flight
      // read may have started before the write committed, so it can return
      // stale rows.
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        const rows = await fetcherRef.current();
        loadedOnce = true;
        if (!cancelled) setState({ rows, loading: false, error: null });
      } catch (e) {
        if (!cancelled) {
          setState((prev) => ({ rows: loadedOnce ? prev.rows : [], loading: false, error: e as Error }));
        }
      }
      running = false;
      if (cancelled) return;
      if (again) {
        again = false;
        void load();
      }
    };

    // Queued as a 0ms macrotask — effectively immediate in the browser, and a
    // drainable timer under fake-timer tests.
    const trigger = () => {
      clearTimeout(timer);
      void load();
    };
    timer = setTimeout(load, 0);

    const onVisible = () => {
      if (document.visibilityState === 'visible') trigger();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', trigger);
    const offMutation = onMutation(trigger);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', trigger);
      offMutation();
    };
  }, [key]);

  return state;
}
